import usePublicOrganization from "./usePublicOrganization";
import {
  getOrgLogo,
  getOrgName,
  getPublicOrgDisplayName,
} from "../utils/publicChurch";

/**
 * Resolve PUBLIC organization branding for donor/member auth screens.
 *
 * Branding-only and frontend-only: it reads public organization data (name,
 * logo, brand color) and never touches auth/session behavior. Keeping this
 * separate from the presentational OrgAuthBrand component lets the component
 * stay pure/testable.
 *
 * Returns:
 *   hasOrgContext - whether an org slug was provided at all (drives org-vs-
 *                   platform branding choice on the auth screen).
 *   loading       - true only while an org context is still resolving (so the
 *                   caller can avoid flashing platform branding).
 *   name/logoUrl/primaryColor - resolved org branding once available.
 */
export default function useOrgAuthBranding(orgSlug) {
  const safeSlug = String(orgSlug || "").trim();
  const hasOrgContext = Boolean(safeSlug);

  const { org, loading } = usePublicOrganization(safeSlug);

  const profile = org?.publicProfile || org?.profile || {};
  const branding = {
    ...(org?.branding && typeof org.branding === "object" ? org.branding : {}),
    ...(profile?.branding && typeof profile.branding === "object" ? profile.branding : {}),
  };

  return {
    orgSlug: safeSlug,
    hasOrgContext,
    // Only "loading" when we actually have a slug and the org hasn't resolved.
    loading: hasOrgContext && loading && !org,
    name: org ? (getPublicOrgDisplayName(org) || getOrgName(org) || "") : "",
    logoUrl: org ? getOrgLogo(org) : "",
    primaryColor: String(branding.primaryColor || org?.primaryColor || "").trim(),
    // Public type fields, so auth screens can use the organization's own wording.
    organizationKind: {
      organizationType: org?.organizationType || "",
      orgType: org?.orgType || "",
      type: org?.type || "",
      appTemplateId: org?.appTemplateId || "",
    },
  };
}
